import { newServer } from 'constants/constants';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface PageIframeProps {
  pageId: string;
}

// Mapa inverso: de ruta a pageId (usado para validar rutas válidas)
// ChemLog - Chemical Logistics
const routeToPageId: { [key: string]: string } = {
  '/': '1',
  '/feature/dispatch': '2',
  '/feature/invoicing': '3',
  '/feature/driver-pay': '4',
  '/feature/reporting': '5',
  '/implementation': '6',
  '/chemlog-demo-video': '7',
  '/support': '9',
};

/**
 * Componente para mostrar páginas HTML en un iframe sin scroll anidado.
 * El iframe ocupa todo el espacio disponible y maneja su propio scroll interno.
 * El script de navegación se inyecta desde el backend para evitar problemas de CORS.
 * Este componente solo escucha los mensajes postMessage del iframe.
 */
const PageIframe: React.FC<PageIframeProps> = ({ pageId }) => {
  const [src, setSrc] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();

  // Escuchar mensajes del iframe (el script de navegación se inyecta desde el backend)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verificar que el mensaje es del tipo NAVIGATION
      if (event.data && event.data.type === 'NAVIGATION') {
        const targetPath = event.data.path;
        // Validar que la ruta existe en nuestro mapa
        if (targetPath && routeToPageId[targetPath]) {
          console.log('[PageIframe] Navegando a:', targetPath);
          navigate(targetPath);
        } else {
          console.log('[PageIframe] Ruta no reconocida:', targetPath);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate]);

  useEffect(() => {
    loadPage();
  }, [pageId]);

  const loadPage = async () => {
    setLoading(true);
    const pageUrl = `${newServer}landing-page/get/page-${pageId}`;

    try {
      const response = await fetch(pageUrl);

      if (response.ok) {
        setSrc(pageUrl);
        setError('');
      } else {
        throw new Error('Page not found');
      }
    } catch (error) {
      setError('Error: Page not found');
      setSrc('');
    } finally {
      setLoading(false);
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white'
      }}>
        <p style={{ color: 'red' }}>{error}</p>
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #FF007F',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
      {src && (
        <iframe
          ref={iframeRef}
          src={src}
          title={`Page ${pageId}`}
          onLoad={handleIframeLoad}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'white',
            display: 'block',
            flex: 1,
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.2s ease-in-out'
          }}
        />
      )}
    </>
  );
};

export default PageIframe;

